import { useState, useEffect, useCallback, useRef } from "react";
import { X, Loader2, Check, User, ChevronLeft, ChevronRight } from "lucide-react";
import { useSelector, useDispatch } from "react-redux";
import { Swiper, SwiperSlide } from "swiper/react";
import SwiperCore, { Navigation, Pagination, Autoplay } from 'swiper';
import useAxiosAdmin from "../../lib/axiosAdmin";
import { setAllDataOfAdminServices } from "../../../../redux/action/admin/services/action";
import { setAllDataOfAdminStaff } from "../../../../redux/action/admin/staff/action";
import VipDatePickerStep from "./VipDatePickerStep";
import NormalDatePickerStep from "./NormalDatePickerStep";

import { prependNewAppointment } from "../../../../redux/action/admin/appointments/action"; 

import "swiper/swiper.min.css";
import "swiper/components/navigation/navigation.min.css";
import "swiper/components/pagination/pagination.min.css";
import { toast } from "react-toastify";
import CustomerPaymentStep from "./CustomerPaymentStep";

SwiperCore.use([Navigation, Pagination, Autoplay]);

const AppointmentModal = ({ onClose, onSuccess, editAppointment }) => {
  const axiosAdmin = useAxiosAdmin();
  const dispatch = useDispatch();

  const { services: servicesList, loaded: servicesLoaded } = useSelector((state) => state.adminServices);
  const { staff: staffList, loaded: staffLoaded } = useSelector((state) => state.adminStaff);

  const [loadingServices, setLoadingServices] = useState(false);
  const [loadingStaff, setLoadingStaff] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedMethod, setSelectedMethod] = useState(null);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [step, setStep] = useState("select"); // select | vipDate
   const [customerPhone, setCustomerPhone] = useState("");

  const [availabilityLoading, setAvailabilityLoading] = useState(false);
  const [availabilityError, setAvailabilityError] = useState("");
  const [availabilityData, setAvailabilityData] = useState(null);
  const [selectedVipDate, setSelectedVipDate] = useState(null);
  const [normalAvailabilityData, setNormalAvailabilityData] = useState(null);
  const [normalAvailabilityLoading, setNormalAvailabilityLoading] = useState(false);
  const [normalAvailabilityError, setNormalAvailabilityError] = useState("");
  const [selectedNormalDate, setSelectedNormalDate] = useState(null);
  const [selectedDateTime, setSelectedDateTime] = useState(null);
  const [selectedVipTime, setSelectedVipTime] = useState(null);
  const [selectedNormalTime, setSelectedNormalTime] = useState(null);

  
    const profile = useSelector(state => state.auth.profile);

  const step2Ref = useRef(null);
  const step3Ref = useRef(null);
  const step4Ref = useRef(null);
  const scrollContainerRef = useRef(null);

  const scrollToRef = (ref) => {
    if (!ref?.current || !scrollContainerRef?.current) return;
    setTimeout(() => {
      const container = scrollContainerRef.current;
      const element = ref.current;
      const containerTop = container.getBoundingClientRect().top;
      const elementTop = element.getBoundingClientRect().top;
      const offset = elementTop - containerTop + container.scrollTop - 24;
      container.scrollTo({ top: offset, behavior: "smooth" });
    }, 100);
  };

  const fetchServices = useCallback(async () => {
    if (servicesLoaded && servicesList.length > 0) return;
    try {
      setLoadingServices(true);
      const res = await axiosAdmin.get("/api/admin/services", { params: { limit: 500 } });
      dispatch(setAllDataOfAdminServices({
        page: res.data.page || 0,
        total: res.data.total || 0,
        pages: res.data.pages || 0,
        services: res.data.services || [],
      }));
    } catch (err) {
      console.error("خطا در دریافت خدمات:", err);
    } finally {
      setLoadingServices(false);
    }
  }, [axiosAdmin, dispatch, servicesLoaded, servicesList.length]);

  const fetchStaff = useCallback(async () => {
    if (staffLoaded && staffList.length > 0) return;
    try {
      setLoadingStaff(true);
      const res = await axiosAdmin.get("/api/admin/staff", { params: { limit: 500 } });
      dispatch(setAllDataOfAdminStaff({
        page: res.data.page || 0,
        total: res.data.total || 0,
        pages: res.data.pages || 0,
        staff: res.data.staff || [],
      }));
    } catch (err) {
      console.error("خطا در دریافت کارکنان:", err);
    } finally {
      setLoadingStaff(false);
    }
  }, [axiosAdmin, dispatch, staffLoaded, staffList.length]);

  const checkVipAvailability = useCallback(async () => {
    if (!selectedService || !selectedType || !selectedMethod || !selectedStaff) {
      toast.warning("لطفاً ابتدا خدمت، نوع، روش و مژه‌کار را انتخاب کنید");
      return;
    }
    try {
      setAvailabilityLoading(true);
      setAvailabilityError("");
      setAvailabilityData(null);
      setSelectedVipDate(null);

      const res = await axiosAdmin.post("/api/admin/appointment/check-availability", {
        serviceId: selectedService._id,
        type: selectedType,
        method: selectedMethod,
        staffId: selectedStaff._id,
        days: 180,
      });

      console.log(res);

      setAvailabilityData(res.data);
      setStep("vipDate");
    } catch (err) {
      console.error("خطا در بررسی ظرفیت VIP:", err);
      const message = err?.response?.data?.message || "خطا در بررسی ظرفیت رزرو VIP";
      setAvailabilityError(message);
      setStep("vipDate");
      toast.error(message);
    } finally {
      setAvailabilityLoading(false);
    }
  }, [axiosAdmin, selectedService, selectedType, selectedMethod, selectedStaff]);


    // ========================
  // تابع اصلی پس از انتخاب نهایی
  // ========================
  const handleFinalSubmit = async ({ user, paymentType }) => {
    if (!selectedDateTime) {
      toast.error("تاریخ و ساعت انتخاب نشده است");
      return;
    }
    const { date, time } = selectedDateTime;

    try {
      const payload = {
        staffInfo: selectedStaff ? { _id: selectedStaff._id } : undefined,
        user: {
          phone: user.phone,
          firstName: user.firstName,
          lastName: user.lastName,
        },
        datetime: { date, time },
        service: {
          id: selectedService._id,
          type: selectedType,
          method: selectedMethod,
        },
        paymentType,
        amount: (getActiveMethods().find((m) => m.key === selectedMethod)?.price || 0),
      };

      const res = await axiosAdmin.post("/api/admin/appointment", payload);

      // 📌 اضافه‌کردن قرار جدید به Redux (بالای لیست)
      if (res?.data?.appointment) {
        dispatch(prependNewAppointment(res.data.appointment));
      }

      toast.success("قرار با موفقیت ثبت شد");
      onClose?.(); // بستن مودال
    } catch (err) {
      console.error(err);
      const msg = err?.response?.data?.message;
      if (msg?.includes("کاربری با این شماره وجود ندارد")) {
        toast.warning("لطفاً نام و نام‌خانوادگی مشتری را وارد کنید.");
      } else {
        toast.error(msg || "خطا در ثبت قرار");
      }
    }
  };


  useEffect(() => {
    setAvailabilityData(null);
    setAvailabilityError("");
    setSelectedVipDate(null);
  }, [selectedService, selectedType, selectedMethod, selectedStaff]);

  useEffect(() => { fetchServices(); }, [fetchServices]);

  useEffect(() => {
    if (selectedMethod === "vip") {
      fetchStaff();
      scrollToRef(step4Ref);
    }
  }, [selectedMethod, fetchStaff]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = "auto"; };
  }, []);

  const getActiveTypes = () => {
    if (!selectedService) return [];
    const types = [];
    if (selectedService.kasht) types.push({ key: "kasht", label: "کاشت" });
    if (selectedService.retoosh) types.push({ key: "retoosh", label: "رتوش" });
    if (selectedService.tarmim) types.push({ key: "tarmim", label: "ترمیم" });
    if (selectedService.lift) types.push({ key: "lift", label: "لیفت" });
    return types;
  };

  const getActiveMethods = () => {
    if (!selectedService) return [];
    const methods = [];
    if (selectedService.normal?.enabled) methods.push({ key: "عادی", label: "عادی", price: selectedService.normal.price });
    if (selectedService.vip?.enabled) methods.push({ key: "vip", label: "VIP", price: selectedService.vip.price });
    return methods;
  };

  const getEligibleStaff = () => {
  if (!selectedService || !selectedType || !selectedMethod) return [];
  return staffList.filter((member) => {
    const serviceEntry = member.services?.find(
      (s) => s.serviceId === selectedService._id
    );
    if (!serviceEntry) return false;
    // نوع خدمت (کاشت، رتوش و ...)
    if (!serviceEntry[selectedType]) return false;
    // بررسی مجوز روش انتخاب‌شده
    if (selectedMethod === "vip") {
      return serviceEntry.vip?.enabled === true;
    }
    if (selectedMethod === "عادی") {
      return serviceEntry.normal?.enabled === true;
    }
    return false;
  });
};

  const checkNormalAvailability = useCallback(async () => {
  if (!selectedService || !selectedType) {
    toast.warning("لطفاً خدمت و نوع آن را انتخاب کنید");
    return;
  }
  try {
    setNormalAvailabilityLoading(true);
    setNormalAvailabilityError("");
    setNormalAvailabilityData(null);
    setSelectedNormalDate(null);

    const res = await axiosAdmin.post("/api/admin/appointment/check-availability-normal", {
      serviceId: selectedService._id,
      type: selectedType,
      days: 180,
    });

    setNormalAvailabilityData(res.data);
    setStep("normalDate");
  } catch (err) {
    const message = err?.response?.data?.message || "خطا در بررسی ظرفیت عادی";
    setNormalAvailabilityError(message);
    setStep("normalDate");
    toast.error(message);
  } finally {
    setNormalAvailabilityLoading(false);
  }
}, [axiosAdmin, selectedService, selectedType]);


  const handleContinue = async () => {
    if (!isComplete) return;
    if (selectedMethod === "vip") {
      await checkVipAvailability();
      return;
    }
    if (selectedMethod === "عادی") {
      await checkNormalAvailability();
      return;
    }
  };

  const activeTypes = getActiveTypes();
  const activeMethods = getActiveMethods();
  const eligibleStaff = selectedMethod === "vip" ? getEligibleStaff() : [];

  const isComplete =
    selectedService &&
    selectedType &&
    selectedMethod &&
    (selectedMethod !== "vip" || selectedStaff);


  return (
    <div onClick={onClose} className="fixed inset-0 overflow-hidden bg-black/60 backdrop-blur-sm flex items-center justify-center z-[1000] p-4 overflow-y-auto">
      
      <div onClick={(e) => e.stopPropagation()} className="bg-white w-full max-w-6xl rounded-2xl shadow-2xl overflow-hidden animate-in fade-in zoom-in duration-300 my-8">

        {/* Header */}
        <div className="bg-gradient-to-r overflow-hidden from-purple-600 to-purple-700 px-6 py-5 flex items-center justify-between sticky top-0 z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-white/20 backdrop-blur-sm flex items-center justify-center">
              <span className="text-2xl">{editAppointment ? "✏️" : "➕"}</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {editAppointment ? "ویرایش قرار" : "افزودن قرار جدید"}
              </h2>
              <p className="text-purple-100 text-sm mt-0.5">اطلاعات قرار را با دقت وارد کنید</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-9 h-9 rounded-lg bg-white/10 hover:bg-white/20 flex items-center justify-center transition-colors group"
          >
            <X className="w-5 h-5 text-white group-hover:rotate-90 transition-transform duration-300" />
          </button>
        </div>

        {/* Content */}
        <div ref={scrollContainerRef} className="p-6 overflow-hidden max-h-[calc(100vh-200px)] overflow-y-auto">

          {/* ── VipDatePickerStep ── */}
          {step === "vipDate" ? (
            <VipDatePickerStep
              availabilityData={availabilityData}
              loading={availabilityLoading}
              error={availabilityError}
              selectedDate={selectedVipDate}
              onSelectDate={(dateObj) => setSelectedVipDate(dateObj)}
              onBack={() => setStep("select")}
              selectedTime1={selectedVipTime}
              onContinue={({ date, jalaaliDate, time }) => {
                setSelectedVipTime(time);
                setSelectedDateTime({ date, jalaaliDate, time });
                setStep("customerPayment");
              }}  // ← این تابع جدید
              axiosAdmin={axiosAdmin}
              phone={customerPhone}
              onPhoneChange={setCustomerPhone}
            />  
          ) :  step === "normalDate" ? (
            <NormalDatePickerStep
              availabilityData={normalAvailabilityData}
              loading={normalAvailabilityLoading}
              error={normalAvailabilityError}
              selectedDate={selectedNormalDate}
              onSelectDate={(dateObj) => setSelectedNormalDate(dateObj)}
              onBack={() => setStep("select")}
              onContinue={({ date, time, staff }) => {          // 👈 باید staff را هم دریافت کنید
                setSelectedNormalTime(time);
                setSelectedDateTime({ date, time });
                setSelectedStaff(staff);                         // 👈 مهم: ذخیرهٔ کارمند انتخاب‌شده
                setStep("customerPayment");
              }}
              axiosAdmin={axiosAdmin}
              serviceId={selectedService?._id}
              type={selectedType}
            />
          ) :  step === "customerPayment" ? (
          <CustomerPaymentStep
            axiosAdmin={axiosAdmin}
            onBack={() => setStep(selectedMethod === "vip" ? "vipDate" : "normalDate")}
            onSubmit={handleFinalSubmit}
            phone={customerPhone}
            // می‌توانید مقادیر اولیه را از ویرایش قرار هم بگیرید
          />
        ) : (
            <>
              {/* مرحله 1: انتخاب خدمت */}
              <div className="mb-8">
                <div className="mb-6">
                  <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center gap-2">
                    <span className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-bold">1</span>
                    خدمت خود را انتخاب کنید
                  </h3>
                  <p className="text-sm text-slate-500 mr-10">یکی از خدمات زیر را برای رزرو انتخاب کنید</p>
                </div>

                {loadingServices ? (
                  <div className="flex flex-col items-center justify-center py-20">
                    <Loader2 className="w-10 h-10 text-purple-500 animate-spin mb-3" />
                    <p className="text-slate-500 text-sm">در حال بارگذاری خدمات...</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {servicesList.map((service) => (
                      <div
                        key={service._id}
                        onClick={() => {
                          setSelectedService(service);
                          setSelectedType(null);
                          setSelectedMethod(null);
                          setSelectedStaff(null);
                          scrollToRef(step2Ref);
                        }}
                        className={`
                          relative cursor-pointer rounded-xl overflow-hidden border-2 transition-all duration-300
                          ${selectedService?._id === service._id
                            ? "border-purple-500 shadow-lg shadow-purple-500/30 scale-105"
                            : "border-slate-200 hover:border-purple-300 hover:shadow-md"}
                        `}
                      >
                        <div className="relative h-40 bg-slate-100">
                          {service.images && service.images.length > 0 ? (
                            <>
                              <Swiper
                                spaceBetween={0}
                                loop={service.images.length > 1}
                                slidesPerView={1}
                                autoplay={{ delay: 2000, disableOnInteraction: false, pauseOnMouseEnter: true }}
                                navigation={{
                                  nextEl: `.next-${service._id}`,
                                  prevEl: `.prev-${service._id}`,
                                }}
                                className="h-full"
                              >
                                {service.images.map((img, idx) => (
                                  <SwiperSlide key={idx}>
                                    <img
                                      src={(img?.includes('https://') || img?.includes('http://')) ? img : 'https://my-sarad-back.liara.run'+img}
                                      alt={service.name}
                                      className="w-full h-full object-cover"
                                    />
                                  </SwiperSlide>
                                ))}
                              </Swiper>
                              <button className={`prev-${service._id} absolute right-2 top-1/2 -translate-y-[30%] z-10 text-white/70 hover:text-white`}>
                                <ChevronRight size={26} />
                              </button>
                              <button className={`next-${service._id} absolute left-2 top-1/2 -translate-y-[30%] z-10 text-white/70 hover:text-white`}>
                                <ChevronLeft size={26} />
                              </button>
                            </>
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-400">
                              <span className="text-4xl">📷</span>
                            </div>
                          )}
                          {selectedService?._id === service._id && (
                            <div className="absolute top-2 left-2 w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center shadow-lg animate-in zoom-in duration-300">
                              <Check className="w-5 h-5 text-white" />
                            </div>
                          )}
                        </div>
                        <div className="p-3 bg-white">
                          <p className="text-sm font-semibold text-slate-800 text-center line-clamp-2">{service.name}</p>
                        </div>
                        <div className="absolute bottom-3 right-3">
                          <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${selectedService?._id === service._id ? "border-purple-600 bg-purple-600" : "border-slate-300 bg-white"}`}>
                            {selectedService?._id === service._id && <div className="w-2 h-2 rounded-full bg-white" />}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* مرحله 2: انتخاب نوع خدمت */}
              {selectedService && activeTypes.length > 0 && (
                <div ref={step2Ref} className="mb-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center gap-2">
                      <span className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-bold">2</span>
                      یکی از این گزینه‌ها را انتخاب کنید
                    </h3>
                    <p className="text-sm text-slate-500 mr-10">نوع خدمت مورد نظر خود را مشخص کنید</p>
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {activeTypes.map((type) => (
                      <div
                        key={type.key}
                        onClick={() => {
                          setSelectedType(type.key);
                          setSelectedMethod(null);
                          setSelectedStaff(null);
                          scrollToRef(step3Ref);
                        }}
                        className={`
                          relative cursor-pointer rounded-xl p-6 border-2 transition-all duration-300
                          ${selectedType === type.key
                            ? "border-purple-500 bg-purple-50 shadow-lg shadow-purple-500/20"
                            : "border-slate-200 bg-white hover:border-purple-300 hover:shadow-md"}
                        `}
                      >
                        <div className="text-center">
                          <p className="text-base font-bold text-slate-800 mb-2">{type.label}</p>
                          <div className="flex justify-center mt-3">
                            <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${selectedType === type.key ? "border-purple-600 bg-purple-600" : "border-slate-300 bg-white"}`}>
                              {selectedType === type.key && <div className="w-3 h-3 rounded-full bg-white" />}
                            </div>
                          </div>
                        </div>
                        {selectedType === type.key && (
                          <div className="absolute bottom-6 left-1/2 -translate-x-1/2 w-7 h-7 rounded-full bg-purple-600 flex items-center justify-center shadow-lg animate-in zoom-in duration-300">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* مرحله 3: انتخاب متد */}
              {selectedService && selectedType && activeMethods.length > 0 && (
                <div ref={step3Ref} className="mb-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center gap-2">
                      <span className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-bold">3</span>
                      نوع خدمت را انتخاب کنید
                    </h3>
                    <p className="text-sm text-slate-500 mr-10">حالت عادی یا VIP را مشخص کنید</p>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 max-w-2xl">
                    {activeMethods.map((method) => (
                      <div
                        key={method.key}
                        onClick={() => {
                          setSelectedMethod(method.key);
                          setSelectedStaff(null);
                        }}
                        className={`
                          relative cursor-pointer rounded-xl p-6 border-2 transition-all duration-300
                          ${selectedMethod === method.key
                            ? "border-purple-500 bg-purple-50 shadow-lg shadow-purple-500/20"
                            : "border-slate-200 bg-white hover:border-purple-300 hover:shadow-md"}
                        `}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-lg font-bold text-slate-800 mb-1">{method.label}</p>
                            <p className="text-sm text-slate-500">
                              قیمت: <span className="font-semibold text-purple-600">{(method.price || 0).toLocaleString()} تومان</span>
                            </p>
                          </div>
                          <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center transition-all duration-300 ${selectedMethod === method.key ? "border-purple-600 bg-purple-600" : "border-slate-300 bg-white"}`}>
                            {selectedMethod === method.key && <div className="w-3 h-3 rounded-full bg-white" />}
                          </div>
                        </div>
                        {selectedMethod === method.key && (
                                                   <div className="absolute top-1/2 -translate-y-1/2 left-6 w-7 h-7 rounded-full bg-purple-600 flex items-center justify-center shadow-lg animate-in zoom-in duration-300">
                            <Check className="w-4 h-4 text-white" />
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* مرحله 4: انتخاب مژه‌کار (فقط VIP) */}
              {selectedService && selectedType && selectedMethod === "vip" && (
                <div ref={step4Ref} className="mb-8 animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <div className="mb-6">
                    <h3 className="text-lg font-bold text-slate-800 mb-2 flex items-center gap-2">
                      <span className="w-8 h-8 rounded-full bg-purple-100 text-purple-700 flex items-center justify-center text-sm font-bold">4</span>
                      مژه‌کار مورد نظر را انتخاب کنید
                    </h3>
                    <p className="text-sm text-slate-500 mr-10">مژه‌کاری که مایلید با آن تایم رزرو کنید نیز انتخاب کنید</p>
                  </div>

                  {loadingStaff ? (
                    <div className="flex flex-col items-center justify-center py-20">
                      <Loader2 className="w-10 h-10 text-purple-500 animate-spin mb-3" />
                      <p className="text-slate-500 text-sm">در حال بارگذاری کارکنان...</p>
                    </div>
                  ) : eligibleStaff.length === 0 ? (
                    <div className="flex flex-col items-center justify-center py-16 text-slate-400">
                      <span className="text-5xl mb-3">👤</span>
                      <p className="text-sm">هیچ کارکنی برای این خدمت یافت نشد</p>
                    </div>
                  ) : (
                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3">
                      {eligibleStaff.map((member) => (
                        <div
                          key={member._id}
                          onClick={() => setSelectedStaff(member)}
                          className={`
                            relative cursor-pointer rounded-xl p-4 border-2 transition-all duration-300 flex items-center gap-3
                            ${selectedStaff?._id === member._id
                              ? "border-purple-500 bg-purple-50 shadow-lg shadow-purple-500/20"
                              : "border-slate-200 bg-white hover:border-purple-300 hover:shadow-md"}
                          `}
                        >
                          <div className={`w-10 h-10 rounded-full flex-shrink-0 flex items-center justify-center text-sm font-bold transition-all duration-300 ${selectedStaff?._id === member._id ? "bg-purple-600 text-white" : "bg-slate-100 text-slate-500"}`}>
                            {member.information?.firstName?.charAt(0) || member.information?.displayName?.charAt(0) || <User className="w-4 h-4" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-semibold text-slate-800 truncate">
                              {member.information?.firstName
                                ? `${member.information.firstName} ${member.information.lastName}`
                                : member.information?.displayName}
                            </p>
                          </div>
                          <div className={`w-5 h-5 rounded-full border-2 flex-shrink-0 flex items-center justify-center transition-all duration-300 ${selectedStaff?._id === member._id ? "border-purple-600 bg-purple-600" : "border-slate-300 bg-white"}`}>
                            {selectedStaff?._id === member._id && <div className="w-2 h-2 rounded-full bg-white" />}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}

        </div>

        {/* Footer - فقط در step === "select" */}
        {step === "select" && (
          <div className="bg-slate-50 overflow-hidden border-t border-slate-200 px-6 py-4 flex items-center justify-between sticky bottom-0">
            <div className="text-sm text-slate-600">
              {isComplete ? (
                <span className="text-green-600 font-medium flex items-center gap-2">
                  <Check className="w-4 h-4" />
                  آماده ادامه
                </span>
              ) : (
                <span>لطفا مراحل را تکمیل کنید</span>
              )}
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={onClose}
                className="px-5 py-2.5 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                لغو
              </button>
              <button
                onClick={handleContinue}
                disabled={!isComplete || availabilityLoading}
                className="px-6 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-purple-600 to-purple-700 hover:from-purple-700 hover:to-purple-800 rounded-lg shadow-md shadow-purple-500/25 hover:shadow-lg hover:shadow-purple-500/30 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
              >
                {availabilityLoading ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span>در حال بررسی...</span>
                  </>
                ) : (
                  <>
                    <span>ادامه</span>
                    <span>←</span>
                  </>
                )}
              </button>
            </div>
          </div>
        )}

      </div>

    </div>
  );
};

export default AppointmentModal;